/**
 * What: Single-source column contract for the Tests resource index. Each
 * column entry carries its serializable shape plus its cell renderer, so
 * definition ids and renderer keys cannot drift. The shell owns table
 * structure; the row controller owns per-row lifecycle preparation.
 * Transport / data-flow: Pure; validated once via defineResourceIndex.
 */
import { studySummaryPresentation } from '../hooks/study-builder/studyAuthoringPresentation'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { defineResourceIndex } from './resource-index/defineResourceIndex'
import { STUDY_TREATMENT_TYPE_OPTIONS } from './study-setup-index'
import { MERCHANT_QUEUE_FILTERS } from './study-setups-page-state'
import { StudySetupCardSummary } from './StudySetupCardSummary'
import type { StudySetupBadgeTone } from './study-setup-index'
import type { StudySetupIndexRow } from './study-setups-page-controller-types'
import { formatDateTime } from '../utils/formatters'
import type { ReactNode } from 'react'

export type StudySetupIndexModel = {
  kind: 'builder-draft'
  draft: Extract<StudySetupIndexRow, { kind: 'builder-draft' }>['draft']
  handleOpenStudy?: () => void
  rowLinkId: string
  actions?: ReactNode
}

/** Neutral status uses the default badge tone. */
function badgeTone(tone: StudySetupBadgeTone) {
  return tone === 'neutral' ? undefined : tone
}

const copy = shopifyComponentCopy.studySetupsPage

function treatmentLabel(model: StudySetupIndexModel) {
  return (
    STUDY_TREATMENT_TYPE_OPTIONS.find((option) => option.value === model.draft.treatmentType)?.label ??
    copy.unavailableValue037
  )
}

const { definition: studySetupsIndexDefinition, renderers: studySetupRenderers } =
  defineResourceIndex<StudySetupIndexModel>({
    id: 'tests',
    resource: { singular: 'test', plural: 'tests' },
    views: MERCHANT_QUEUE_FILTERS,
    filters: [
      { key: 'product', label: copy.productHeading023, multiple: true },
      { key: 'audience', label: copy.audienceHeading014, multiple: true },
      { key: 'treatment-type', label: copy.treatmentTypeHeading028, multiple: true },
      { key: 'lifecycle', label: copy.statusHeading013, multiple: true },
    ],
    sortOptions: [
      { label: copy.sortProductTitleAscending026, value: 'product-title asc' },
      { label: copy.sortProductTitleDescending027, value: 'product-title desc' },
      { label: copy.sortStudyTitleAscending018, value: 'study-title asc' },
      { label: copy.sortStudyTitleDescending019, value: 'study-title desc' },
      { label: copy.sortStatusAscending020, value: 'status asc' },
      { label: copy.sortTreatmentsAscending030, value: 'treatment-count asc' },
      { label: copy.sortTreatmentsDescending031, value: 'treatment-count desc' },
      { label: copy.sortCreatedDescending021, value: 'created desc' },
      { label: copy.sortCreatedAscending022, value: 'created asc' },
    ],
    searchLabel: copy.searchStudies011,
    metadata: { pagination: 'client' },
    columns: [
      {
        id: 'thumbnail',
        heading: '',
        kind: 'image',
        listSlot: 'primary',
        render: (model) => {
          if (!model.draft.productImageUrl) return null
          return (
            <StudySetupCardSummary
              product={{
                title: model.draft.productTitle ?? copy.unavailableValue037,
                imageUrl: model.draft.productImageUrl,
                imageAlt: model.draft.productImageAlt,
              }}
            />
          )
        },
      },
      {
        id: 'product',
        heading: copy.productHeading023,
        kind: 'custom',
        listSlot: 'secondary',
        showInList: false,
        render: (model) => {
          const productTitle = model.draft.productTitle ?? copy.unavailableValue037
          const { handleOpenStudy, rowLinkId } = model
          const title = <s-text>{productTitle}</s-text>
          const linkedTitle = (
            <s-link id={rowLinkId} onClick={handleOpenStudy}>
              {productTitle}
            </s-link>
          )
          const content = handleOpenStudy ? linkedTitle : title
          return (
            <div
              onClick={(event) => event.stopPropagation()}
              style={{ minWidth: 200, maxWidth: 280, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              {content}
            </div>
          )
        },
      },
      {
        id: 'study',
        heading: copy.studyHeading012,
        kind: 'text',
        listSlot: 'secondary',
        render: (model) => (
          <div className="shopify-tests-index__study">
            <div className="shopify-tests-index__study-title">
              <s-text>{model.draft.title || copy.untitledStudy025}</s-text>
            </div>
            {model.draft.source === 'opportunity' && (
              <s-text color="subdued">{shopifyComponentCopy.preparedSetupsPanel.opportunityDraftLabel}</s-text>
            )}
          </div>
        ),
      },
      {
        id: 'treatment-type',
        heading: copy.treatmentTypeHeading028,
        kind: 'text',
        listSlot: 'labeled',
        showInList: false,
        render: (model) => <s-text>{treatmentLabel(model)}</s-text>,
      },
      {
        id: 'treatments',
        heading: copy.treatmentsHeading029,
        kind: 'numeric',
        listSlot: 'labeled',
        showInList: false,
        render: (model) => <s-text>{model.draft.versionCount ?? copy.unavailableValue037}</s-text>,
      },
      {
        id: 'status',
        heading: copy.statusHeading013,
        kind: 'badge',
        listSlot: 'inline',
        render: (model) => (
          <div className="shopify-tests-index__status">
            <s-badge tone={badgeTone(studySummaryPresentation(model.draft).tone)}>
              {studySummaryPresentation(model.draft).label}
            </s-badge>
          </div>
        ),
      },
      {
        id: 'result',
        heading: shopifyComponentCopy.testsIndex.resultHeading,
        kind: 'text',
        listSlot: 'labeled',
        showInList: false,
        // Result meaning and merchant wording come from the checked server summary.
        render: (model) => <s-text>{model.draft.summary?.result.label ?? copy.unavailableValue037}</s-text>,
      },
      {
        id: 'created',
        heading: copy.createdHeading015,
        kind: 'date',
        listSlot: 'labeled',
        showInList: false,
        render: (model) => (
          <s-text color="subdued">
            {formatDateTime(model.draft.createdAt ?? model.draft.updatedAt, copy.unavailableValue037)}
          </s-text>
        ),
      },
    ],
  })

export { studySetupsIndexDefinition, studySetupRenderers }
